"use strict";
/* globals module */
/**
 * @module calendarEventsClient
 * @description  CalendarEvents Client provides an easy way to consume CalendarEvents REST API end-points. In order to obtain needed routes `calendarEventsClient` uses `calendarEventsRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../../httpApi");
var __1 = require("../../");
var CalendarEventsClient = /** @class */ (function () {
    function CalendarEventsClient(calendarEventsRoute, apiClient) {
        this.calendarEventsRoute = calendarEventsRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(CalendarEventsClient.prototype, "routeDefinition", {
        get: function () {
            return this.calendarEventsRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of CalendarEvent resources matching the given criteria.
     * @method
     * @param options Query resource GetCalendarEventOptions object.
     * @param calendarId Calendar Id which uniquely identifies Calendar resource.
     * @returns A promise that is resolved once the find action has been performed.
     * @example calendarEventsClient.find(calendarId, {
                    pageNumber: 1,
                    pageSize: 10,
                    orderBy: '<field>',
                    orderDirection: '<asc|desc>',
                    search: '<search-phrase>',
                    ids: <event-identifiers>,
                    ownerIds: <event-owner-identifiers>,
                    statusIds: <event-status-identifiers>,
                    typeIds: <event-type-identifiers>,
                    from: <start-date>,
                    to: <end-date>
                })
                .then(function (collection) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.find = function (calendarId, options) {
        return this.apiClient.get(this.routeDefinition.find(calendarId, options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the CalendarEvent resource.
     * @method
     * @param calendarId Calendar Id which uniquely identifies Calendar resource.
     * @param id CalendarEvent id which uniquely identifies CalendarEvent resource that needs to be retrieved.
     * @param options Query resource GetRequstOptions object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example calendarEventsClient.get(calendarId, id)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.get = function (calendarId, id, options) {
        return this.apiClient.get(this.routeDefinition.get(calendarId, id, options));
    };
    /**
     * Returns a promise that is resolved once the getByEmailOrFullName action has been performed. Success response returns the CalendarEvent resource
     * @method
     * @param calendarId calendarId which uniquely identifies Calendar resource.
     * @param eventId CalendarEvent id which uniquely identifies CalendarEvent resource.
     * @param emailOrFullName Email or full name.
     * @param securityToken Security token.
     * @param options Query resource options object.
     * @example calendarEventsClient.get(calendarId, id, email@example.com)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.getByEmailOrFullName = function (calendarId, eventId, emailOrFullName, securityToken, options) {
        var headers;
        headers['securityToken'] = securityToken;
        return this.apiClient.get(this.routeDefinition.getByEmailOrFullName(calendarId, eventId, emailOrFullName, options), headers);
    };
    /**
     * Returns a promise that is resolved once the link CalendarEvent action has been performed. This action links an existing calendarEvent resource with an existing Calendar resource.
     * @method
     * @param calendarId Calendar id which uniquely identifies the calendar resource to be linked.
     * @param data A calendarEvent resource to be linked.
     * @returns A promise that is resolved once the create CalendarEvent action has been performed.
     * @example calendarEventsClient.link(calendarId, event)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.link = function (calendarId, data) {
        return this.apiClient.post(this.routeDefinition.link(calendarId, data), {});
    };
    /**
     * Returns a promise that is resolved once the update CalendarEvent action has been performed. This action updates a CalendarEvent resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEvent);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A Calendar Event  object used to update specified CalendarEvent resource.
     * @param calendarId calendarId which uniquely identifies Calendar resource.
     * @returns A promise that is resolved once the update CalendarEvent action has been performed.
     * @example calendarEvent is a resource previously fetched using get action.
                calendarEvent.title = '<title>';
                calendarEventsClient.update(calendarid, calendarEvent)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.update = function (calendarId, data) {
        return this.apiClient.put(this.routeDefinition.update(calendarId, data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will unlink a CalendarEvent resource from the specified calendar if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEvent);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data A calendarEvent resource to be unlinked.
     * @param eventId CalendarEvent id which uniquely identifies CalendarEvent resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example calendarEventsClient.unlink(calendarid, data)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventsClient.prototype.unlink = function (calendarId, data) {
        return this.apiClient.delete(this.routeDefinition.unlink(calendarId, data));
    };
    var _a, _b;
    CalendarEventsClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.CalendarEventsRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.CalendarEventsRoute !== "undefined" && __1.CalendarEventsRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], CalendarEventsClient);
    return CalendarEventsClient;
}());
exports.CalendarEventsClient = CalendarEventsClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
